import { useState } from 'react';
import { C, SERIF, mono } from '../theme';
import { Icon } from '../icons';
import { Register } from '../components/Register';
import { LedgerFilter } from '../components/LedgerFilter';
import { Loading, Empty } from '../components/States';
import { useToast } from '../components/Toast';
import { api, ApiError } from '../api';
import type { LedgerScope, TenancyDetail, TenancyLedger } from '../api';
import { useAsync } from '../useAsync';
import { inr, fmtDate } from '../format';
import { buildRegister } from '../register';
import { EditTenancy } from './forms/EditTenancy';
import { RecordPayment } from './forms/RecordPayment';
import { InviteTenant } from './forms/InviteTenant';
import { TenancyDocuments, TenancyDates, TenancyRequests } from './tenancy/TenancyPanels';

interface Combined {
  detail: TenancyDetail | null;
  ledger: TenancyLedger | null;
}

type Sheet = 'edit' | 'payment' | 'invite' | null;
type Tab = 'ledger' | 'documents' | 'dates' | 'maintenance';
const TABS: Array<[Tab, string]> = [
  ['ledger', 'Ledger'],
  ['documents', 'Documents'],
  ['dates', 'Dates'],
  ['maintenance', 'Maintenance'],
];

/**
 * Tenancy detail — the live ledger + the Advanced-lines toggle + reminder + CA
 * export. "Advanced lines" maps to the tenancy's escalation toggle: flipping it
 * PATCHes /tenancies/:id and reloads, so the server-recomputed escalation shows.
 */
export function OwnerDetail({
  tenancyId,
  onBack,
  initialSheet,
}: {
  tenancyId: string;
  onBack: () => void;
  /** Open straight into a sheet, e.g. from the Finish-setting-up card. */
  initialSheet?: Sheet;
}) {
  const notify = useToast();
  const [busyToggle, setBusyToggle] = useState(false);
  const [busyRemind, setBusyRemind] = useState(false);
  const [sheet, setSheet] = useState<Sheet>(initialSheet ?? null);
  const [tab, setTab] = useState<Tab>('ledger');
  const [scope, setScope] = useState<LedgerScope>({}); // {} → current FY (default)

  const { data, loading, error, reload } = useAsync<Combined>(async () => {
    const [detail, ledger] = await Promise.all([
      api.tenancy(tenancyId).catch(() => null),
      api.ledger(tenancyId, scope).catch(() => null),
    ]);
    return { detail, ledger };
  }, [tenancyId, scope]);

  const tenancy = data?.detail?.tenancy ?? data?.ledger?.tenancy ?? null;
  const detail = data?.detail ?? null;
  const ledger = data?.ledger ?? null;
  const advanced = !!tenancy?.escalationEnabled;
  const tdsRatePct = tenancy ? Math.round(tenancy.tdsRateBps / 100) : 10;
  const escPct = tenancy?.escalationPctBps ? tenancy.escalationPctBps / 100 : null;

  const register = ledger
    ? buildRegister({
        periods: ledger.periods,
        payments: ledger.payments,
        openingBalance: ledger.openingBalance,
        tdsRatePct,
      })
    : [];

  async function toggleAdvanced() {
    if (!tenancy) return;
    setBusyToggle(true);
    try {
      await api.updateTenancy(tenancyId, { escalationEnabled: !advanced });
      notify(!advanced ? 'Advanced lines on. Escalation applied.' : 'Advanced lines off');
      reload();
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'Could not update the tenancy');
    } finally {
      setBusyToggle(false);
    }
  }

  async function sendReminder() {
    setBusyRemind(true);
    try {
      await api.sendReminder(tenancyId);
      notify(`Reminder sent to ${detail?.tenantName ?? 'tenant'}`);
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'Could not send the reminder');
    } finally {
      setBusyRemind(false);
    }
  }

  function exportCa() {
    window.open(api.caExportUrl(tenancyId, scope), '_blank', 'noopener');
    notify('Statement export opened');
  }

  const title = detail?.tenantName ?? 'Tenancy';
  const unitLabel = detail?.unitLabel ?? '';

  const modeLabel = advanced ? 'Advanced' : tenancy?.tdsEnabled ? 'Simple + TDS' : 'Simple';

  return (
    <div style={{ margin: '-22px -26px -28px', background: C.bg, minHeight: 560 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, padding: '20px 24px 0' }}>
        <div>
          <div onClick={onBack} style={{ fontSize: 12.5, color: C.accent, marginBottom: 4, cursor: 'pointer', fontWeight: 600 }}>
            ← Tenancies{unitLabel ? ` · ${unitLabel}` : ''}
          </div>
          <h2 style={{ fontFamily: SERIF, fontSize: 22.5, margin: '0 0 3px', fontWeight: 600 }}>{title}</h2>
          {tenancy && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, margin: '6px 0 2px' }}>
              {([
                ['Term', tenancy.startDate ? `${fmtDate(tenancy.startDate)}${tenancy.endDate ? ` – ${fmtDate(tenancy.endDate)}` : ''}` : '–'],
                ['Rent', `${inr(tenancy.rentAmount)}/mo`],
                ['Deposit', inr(tenancy.depositAmount)],
                ['Mode', modeLabel],
              ] as const).map(([k, v], i) => (
                <span key={i} style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 8, padding: '5px 10px', fontSize: 12.5, color: C.ink2 }}>
                  {k} <b style={{ color: C.ink, fontWeight: 600 }}>{v}</b>
                </span>
              ))}
            </div>
          )}
        </div>
        <button onClick={sendReminder} disabled={busyRemind} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: C.accent, color: '#fff', border: 0, borderRadius: 9, padding: '9px 14px', fontWeight: 600, fontSize: 13.3, cursor: busyRemind ? 'default' : 'pointer', opacity: busyRemind ? 0.7 : 1 }}>
          <Icon n="chat" size={15} color="#fff" /> {busyRemind ? 'Sending…' : 'Send reminder'}
        </button>
      </div>

      {/* Manage actions */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '14px 24px 0' }}>
        {([
          ['payment', 'rupee', 'Record payment'],
          ['edit', 'gear', 'Edit terms'],
          ['invite', 'mail', 'Invite tenant'],
        ] as const).map(([key, icon, label]) => (
          <button
            key={key}
            onClick={() => setSheet(key)}
            disabled={!tenancy}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: C.card, color: C.ink2, border: `1px solid ${C.line}`, borderRadius: 9, padding: '8px 13px', fontWeight: 600, fontSize: 13, cursor: tenancy ? 'pointer' : 'default', opacity: tenancy ? 1 : 0.5 }}
          >
            <Icon n={icon} size={15} color={C.accent} /> {label}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 2, padding: '14px 24px 0', borderBottom: `1px solid ${C.line}` }}>
        {TABS.map(([key, label]) => {
          const on = tab === key;
          return (
            <button
              key={key}
              onClick={() => setTab(key)}
              style={{ padding: '8px 14px', fontSize: 13.3, fontWeight: 600, color: on ? C.accent : C.muted, background: 'transparent', border: 0, borderBottom: `2px solid ${on ? C.accent : 'transparent'}`, marginBottom: -1, cursor: 'pointer' }}
            >
              {label}
            </button>
          );
        })}
      </div>

      {tab === 'documents' ? (
        <div style={{ padding: '18px 24px 28px' }}>
          <TenancyDocuments tenancyId={tenancyId} tenantName={detail?.tenantName} />
        </div>
      ) : tab === 'dates' ? (
        <div style={{ padding: '18px 24px 28px' }}>
          <TenancyDates tenancyId={tenancyId} />
        </div>
      ) : tab === 'maintenance' ? (
        <div style={{ padding: '18px 24px 28px' }}>
          <TenancyRequests tenancyId={tenancyId} />
        </div>
      ) : (
      <div style={{ padding: '14px 24px 24px' }}>
        {/* Advanced-lines toggle */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12.8, color: C.muted, marginBottom: 10 }}>
          <span>Advanced lines</span>
          <button
            onClick={toggleAdvanced}
            disabled={busyToggle || !tenancy}
            aria-label="toggle advanced"
            style={{ width: 34, height: 19, borderRadius: 999, background: advanced ? C.accent : '#cdd3cc', position: 'relative', border: 0, cursor: tenancy ? 'pointer' : 'default', flex: 'none', transition: 'background .15s', opacity: busyToggle ? 0.6 : 1 }}
          >
            <span style={{ position: 'absolute', top: 2, left: advanced ? 17 : 2, width: 15, height: 15, borderRadius: 999, background: '#fff', transition: 'left .15s' }} />
          </button>
          <b style={{ color: C.ink }}>{advanced ? 'on' : 'off'}</b>:{' '}
          {advanced
            ? `${escPct ?? 5}% escalation now applied to base rent.`
            : 'single rent figure; TDS is the only adjustment.'}
        </div>

        {loading && !ledger ? (
          <Loading label="Loading ledger…" minHeight={260} />
        ) : error || !ledger ? (
          <Empty icon="ledger" title="Ledger unavailable" hint="We couldn't load this tenancy's ledger." minHeight={260}>
            <button onClick={reload} style={retryBtn}>Retry</button>
          </Empty>
        ) : (
          <>
            {ledger.window && ledger.window.fyOptions.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
                <LedgerFilter fyOptions={ledger.window.fyOptions} value={scope} onChange={setScope} />
                <span style={{ fontSize: 11.8, color: C.muted }}>Running balance · net of {tdsRatePct}% TDS</span>
              </div>
            )}
            {register.length === 0 ? (
              <Empty icon="ledger" title="No entries in this period" hint="Pick another financial year, or All." minHeight={200} />
            ) : (
              <>
                <Register entries={register} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, fontSize: 13, color: C.muted, flexWrap: 'wrap', gap: 8 }}>
                  <span>
                    Balance outstanding · <b style={{ ...mono(), color: ledger.closingBalance > 0 ? C.red : C.hi }}>{inr(ledger.closingBalance)}</b>
                    {ledger.overdueDays != null && ledger.overdueDays > 0 ? ` (${ledger.overdueDays} days overdue)` : ''}
                  </span>
                  <span onClick={exportCa} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, color: C.accent, fontWeight: 600, cursor: 'pointer' }}>
                    <Icon n="doc" size={15} /> Export statement (CSV)
                  </span>
                </div>
              </>
            )}
          </>
        )}
      </div>
      )}

      {sheet === 'payment' && (
        <RecordPayment
          tenancyId={tenancyId}
          tenantName={detail?.tenantName}
          onClose={() => setSheet(null)}
          onRecorded={reload}
        />
      )}
      {sheet === 'edit' && tenancy && (
        <EditTenancy
          tenancy={tenancy}
          tenantName={detail?.tenantName}
          onClose={() => setSheet(null)}
          onSaved={reload}
        />
      )}
      {sheet === 'invite' && tenancy && (
        <InviteTenant
          partyId={tenancy.tenantPartyId}
          tenantName={detail?.tenantName}
          onClose={() => setSheet(null)}
          onSaved={reload}
        />
      )}
    </div>
  );
}

const retryBtn: React.CSSProperties = {
  marginTop: 4,
  border: `1px solid ${C.line}`,
  background: C.card,
  color: C.accent,
  fontWeight: 600,
  fontSize: 13,
  padding: '8px 16px',
  borderRadius: 9,
  cursor: 'pointer',
};
